/**
 * Auth + admin gate (free / Spark plan — no Cloud Functions, no custom claims).
 *
 * Flow:
 *  1. Firebase Auth email/password sign-in.
 *  2. On any auth state change we verify admin status by READING the caller's
 *     OWN admin doc: `admins/{uid}`. Its mere existence grants admin rights
 *     (security rules let a signed-in user read only their own admins/{uid}).
 *     - doc exists            → status "admin".
 *     - doc missing OR error  → status "not-authorized" AND sign the user out,
 *                               so a non-admin never sits in a signed-in state.
 *  3. Routes are protected by <RequireAdmin> which renders the hard-reject
 *     screen for "not-authorized".
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut as fbSignOut,
  type User as FbUser,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { paths, SESSION_MAX_AGE_MS } from '@mitang/shared';
import { auth, db } from './firebase';

export type AuthStatus =
  | 'loading' // resolving initial auth state
  | 'signed-out' // no user
  | 'not-authorized' // signed in, but no admins/{uid} doc
  | 'admin'; // signed in AND admins/{uid} doc exists

interface AuthState {
  status: AuthStatus;
  user: FbUser | null;
  email: string | null;
}

interface AuthContextValue extends AuthState {
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [state, setState] = useState<AuthState>({
    status: 'loading',
    user: null,
    email: null,
  });

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setState({ status: 'signed-out', user: null, email: null });
        return;
      }
      // SESSION EXPIRY: admins must re-login every 10 days (authTime = the
      // moment of the last real sign-in, from the Firebase token).
      try {
        const res = await user.getIdTokenResult();
        const authTime = Date.parse(res.authTime);
        if (Number.isFinite(authTime) && Date.now() - authTime > SESSION_MAX_AGE_MS) {
          setState({ status: 'signed-out', user: null, email: null });
          await fbSignOut(auth).catch(() => undefined);
          return;
        }
      } catch {
        // Offline — skip the age check rather than lock the admin out.
      }
      try {
        // Admin identity = existence of the caller's own admins/{uid} doc.
        const snap = await getDoc(doc(db, paths.admin(user.uid)));
        if (snap.exists()) {
          setState({ status: 'admin', user, email: user.email });
        } else {
          // Not an admin: hard-reject and drop the session so no non-admin
          // ever lingers signed in.
          setState({ status: 'not-authorized', user, email: user.email });
          await fbSignOut(auth).catch(() => undefined);
        }
      } catch {
        // Read failed (rules / network) → treat as not authorized and sign out.
        setState({ status: 'not-authorized', user, email: user.email });
        await fbSignOut(auth).catch(() => undefined);
      }
    });
    return unsub;
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      signIn: async (email, password) => {
        await signInWithEmailAndPassword(auth, email, password);
        // onAuthStateChanged handles admin-doc verification.
      },
      signOut: async () => {
        await fbSignOut(auth);
      },
    }),
    [state],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
